import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Empty, notification, Spin } from 'antd'
import styled from 'styled-components'
import { CommonUtility, DateUtility, VideosService } from 'utility'
import {
  CustomTable,
  FilterButton,
  FlexRow,
  FlexRowBetween,
  InputSearchField,
  LoaderBar,
  PrimaryButton,
  SortButton,
  ToggleViewButton,
  UserAvatarWithName,
  VideoWithTitle,
} from 'components'
import {
  GetDeletedVideosList,
  GetFlaggedVideosList,
  GetVideosList,
  useDebounce,
} from 'hooks'
import { ReviewReportedVideoModal, VideoModal } from './modal'
import { MagnifyingGlass } from 'phosphor-react'
import { VideoCard } from './details'
import { useLocation, useNavigate } from 'react-router-dom'
import { ActionBox } from 'components/ActionBox'
import { StyledCheckbox } from 'components/StyleCloseIcon'
import { VideoGridView } from './grid-view'

const SearchContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  width: 100%;
  padding: 12px;
`

const SearchBox = styled.div`
  display: flex;
  align-items: center;
  border: 1px solid #b3b3b3;
  border-radius: 6px;
  padding: 8px 12px;
  width: 280px;
`

const SearchInput = styled.input`
  border: none;
  outline: none;
  background: transparent;
  flex: 1;
  font-size: 14px;
`
const VideoCardContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-wrap: wrap;
`

const commonColumns = [
  {
    title: 'Creator Name',
    dataIndex: 'user',
    key: 'user',
    width: 150,
    render: (_, record) => (
      <UserAvatarWithName
        id={record?.user?.fid}
        title={CommonUtility.truncateString(record?.user?.name, 20)}
        url={record?.user?.image}
      />
    ),
  },
  {
    title: 'Competition',
    dataIndex: 'competition_name',
    key: 'competition_name',
    width: 100,
    render: value => <span>{CommonUtility.truncateString(value, 40)}</span>,
  },
  {
    title: 'Round',
    dataIndex: 'round_number',
    key: 'round_number',
    align: 'center',
    width: 60,
  },
  {
    title: 'Upload Date',
    dataIndex: 'createdAt',
    key: 'createdAt',
    width: 85,
    render: value => <span>{DateUtility.formatDate(new Date(value))}</span>,
  },
  {
    title: 'Votes',
    dataIndex: 'votes_count',
    key: 'votes_count',
    align: 'center',
    width: 60,
  },
  {
    title: 'Views',
    dataIndex: 'views_count',
    key: 'views_count',
    align: 'center',
    width: 60,
  },
  {
    title: 'Comment',
    dataIndex: 'comment_count',
    key: 'comment_count',
    align: 'center',
    width: 70,
  },
]
export const VideosList = ({ showSearchBar = false }) => {
  const location = useLocation()
  const isPopular = location.state?.for === 'popular'
  const {
    data,
    filter,
    pageChanged,
    total,
    loading,
    setData,
    setTotal,
    updateFilter,
    pageSizeChanged,
  } = GetVideosList(isPopular ? 'popular' : 'newest')
  const [view, setView] = useState('grid')
  const [openModal, setOpenModal] = useState({
    open: false,
    item: {},
  })
  const [search, setSearch] = useState()
  const finalSearch = useDebounce(search, 1000)

  useEffect(() => {
    if (finalSearch?.trim() || finalSearch?.trim() === '') {
      updateFilter('search', finalSearch?.trim())
    }
  }, [finalSearch])

  const handleSort = value => {
    updateFilter('sort', value)
  }

  const columns = useMemo(
    () => [
      {
        title: 'Video',
        dataIndex: 'video',
        key: 'title',
        width: 150,
        render: (_, record) => (
          <VideoWithTitle
            onClick={() => onOpen(record)}
            thumbnailUrl={record?.thumbnail_url}
            title={record?.title}
          />
        ),
      },
      ...commonColumns,
      {
        title: 'Action',
        dataIndex: 'Action',
        key: 'Action',
        align: 'right',
        width: 100,
        render: (_, record) => (
          <div className="d-flex">
            <ActionBox item={record} onDelete={() => onDelete(record?._id)} />
          </div>
        ),
      },
    ],
    [data],
  )

  const onOpen = video => {
    setOpenModal({
      open: true,
      item: video,
    })
  }

  const onDelete = async id => {
    try {
      await VideosService.toggleBlock(id, false)
      setData(prevData =>
        prevData.filter(item => String(item._id) !== String(id)),
      )
      setTotal(prev => prev - 1)
    } catch (error) {
      console.log('error', error)
    }
  }

  return (
    <VideoCardContainer className="row">
      {showSearchBar && (
        <SearchContainer>
          <SearchBox>
            <MagnifyingGlass size={24} color="#b3b3b3" />
            <SearchInput type="text" placeholder="Search..." />
          </SearchBox>
        </SearchContainer>
      )}
      <FlexRowBetween className="mb-3">
        <FlexRow>
          <SortButton
            className="me-3"
            value={filter?.sort}
            onSelect={value => handleSort(value)}
          />
          <InputSearchField
            placeholder="Search here..."
            onChange={val => setSearch(val)}
            className="ms-2 w-50"
          />
        </FlexRow>
        <ToggleViewButton onToggle={view => setView(view)} />
      </FlexRowBetween>
      <>
        {view === 'grid' ? (
          <VideoGridView
            data={data}
            onOpen={onOpen}
            onRestore={onDelete}
            filter={filter}
            total={total}
            pageChanged={pageChanged}
            pageSizeChanged={pageSizeChanged}
          />
        ) : (
          <div className="mb-3">
            <CustomTable
              data={data}
              columns={columns}
              pageChanged={pageChanged}
              pageSizeChanged={pageSizeChanged}
              page={filter?.page}
              updateFilter={updateFilter}
              pageSize={filter?.limit}
              loading={loading}
              total={total}
              hideSorting
            />
          </div>
        )}
      </>
      {!loading && !data.length && <Empty />}
      {openModal.open && (
        <VideoModal
          visible
          onClose={() => setOpenModal({ open: false, item: {} })}
          item={openModal.item}
        />
      )}
    </VideoCardContainer>
  )
}

export const CommonVideosList = ({
  showSearchBar = true,
  data = [],
  loading,
  setData,
  loadMore,
  hasMore,
}) => {
  const [processing, setProcessing] = useState(false)
  const [openModal, setOpenModal] = useState({
    open: false,
    item: {},
  })
  const observerRef = useRef(null) // Reference for the Intersection Observer target

  const onOpen = video => {
    setOpenModal({
      open: true,
      item: video,
    })
  }

  const toggleBlock = async (id, active) => {
    try {
      setProcessing(true)
      await VideosService.toggleBlock(id, active)
      setData(prevData =>
        prevData.filter(item => String(item._id) !== String(id)),
      )
    } catch (error) {
      notification.error({ message: 'Something went wrong' })
    } finally {
      setProcessing(false)
    }
  }
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        if (
          entries[0].isIntersecting &&
          data?.length !== 0 &&
          !loading &&
          hasMore
        ) {
          loadMore()
        }
      },
      { threshold: 1.0 },
    )

    if (observerRef.current) {
      observer.observe(observerRef.current)
    }

    return () => {
      if (observerRef.current) {
        observer.unobserve(observerRef.current)
      }
    }
  }, [loading, loadMore])
  return (
    <VideoCardContainer className="row video-card-container">
      {((loading && data?.length === 0) || processing) && <LoaderBar />}

      {!processing && (
        <>
          {showSearchBar && (
            <SearchContainer>
              <SearchBox>
                <MagnifyingGlass size={24} color="#b3b3b3" />
                <SearchInput type="text" placeholder="Search..." />
              </SearchBox>
            </SearchContainer>
          )}

          {data.map(ele => (
            <div
              key={ele._id}
              className="col-12 col-md-6 col-lg-6 col-xl-4 mb-5"
            >
              <VideoCard
                item={ele}
                onOpen={() => onOpen(ele)}
                toggleBlock={toggleBlock}
              />
            </div>
          ))}

          {/* Infinite Scroll Loader (Only show when loading and not processing) */}
          {!processing && (
            <div
              ref={observerRef}
              className="text-center mt-4"
              style={{ height: '50px' }}
            >
              {loading ? <Spin /> : ''}
            </div>
          )}

          {/* Show Empty when no data */}
          {!loading && !data.length && <Empty />}
        </>
      )}

      {openModal.open && (
        <VideoModal
          visible
          onClose={() => setOpenModal({ open: false, item: {} })}
          item={openModal.item}
        />
      )}
    </VideoCardContainer>
  )
}

export const FlaggedVideosList = () => {
  const {
    data: flaggedVideoData,
    filter,
    pageChanged,
    pageSizeChanged,
    total,
    setTotal,
    loading,
    refetch,
    updateFilter,
  } = GetFlaggedVideosList()
  const [openModal, setOpenModal] = useState({
    open: false,
    item: {},
  })
  const [openReviewModal, setOpenReviewModal] = useState({
    open: false,
    data: null,
    mode: 'single',
  })
  const navigate = useNavigate()
  const [selected, setSelected] = useState([])
  const [data, setData] = useState([])
  const [view, setView] = useState('grid')
  const [search, setSearch] = useState()
  const finalSearch = useDebounce(search, 1000)

  useEffect(() => {
    if (finalSearch?.trim() || finalSearch?.trim() === '') {
      updateFilter('search', finalSearch?.trim())
    }
  }, [finalSearch])

  const onDelete = async id => {
    try {
      await VideosService.toggleBlock(id, false)
      setData(data.filter(ele => ele._id !== id))
      setTotal(prev => prev - 1)
    } catch (error) {
      notification.error({ message: 'Something went wrong' })
    }
  }
  const onOpen = video => {
    setOpenModal({
      open: true,
      item: video,
    })
  }
  useEffect(() => {
    if (flaggedVideoData) {
      setData(flaggedVideoData)
    }
  }, [flaggedVideoData])

  const handleCheckboxChange = record => {
    setSelected(prev =>
      prev.includes(record?._id)
        ? prev.filter(id => id !== record?._id)
        : [...prev, record?._id],
    )
  }

  const columns = useMemo(
    () => [
      {
        title: 'Flag',
        dataIndex: 'flag',
        key: 'flag',
        width: 35,
        render: (_, record) => (
          <StyledCheckbox
            onChange={() => handleCheckboxChange(record)}
            checked={selected?.includes(record?._id)}
          />
        ),
      },
      {
        title: 'Video',
        dataIndex: 'video',
        key: 'title',
        width: 150,
        render: (_, record) => (
          <VideoWithTitle
            onClick={() => onOpen(record)}
            thumbnailUrl={record?.thumbnail_url}
            title={record?.title}
          />
        ),
      },
      ...commonColumns.filter(
        ele =>
          ele &&
          !['Votes', 'Views', 'Upload Date', 'Comment'].includes(ele.title),
      ),
      {
        title: 'Reason',
        dataIndex: 'resolved_comment',
        key: 'resolved_comment',
        width: 100,
        render: text => <p>{CommonUtility.truncateString(text || '-', 20)}</p>,
      },
      {
        title: 'Reported By',
        dataIndex: 'flagged_by',
        key: 'flagged_by',
        width: 100,
        render: (_, record) => (
          <UserAvatarWithName
            id={record?.flagged_by?.fid}
            title={record?.flagged_by?.name}
            url={record?.flagged_by?.image}
          />
        ),
      },
      {
        title: 'Reported on',
        dataIndex: 'flagged_on',
        key: 'flagged_on',
        width: 80,
        render: value => <span>{DateUtility.formatDate(new Date(value))}</span>,
      },
      {
        title: 'Action',
        dataIndex: 'Action',
        key: 'Action',
        align: 'right',
        width: 70,
        render: (_, record) => (
          <ActionBox
            item={record}
            withOutConfirmation
            onReview={() =>
              setOpenReviewModal({
                open: true,
                data: record,
                mode: 'single',
              })
            }
          />
        ),
      },
      {
        title: 'Action',
        dataIndex: 'Action',
        key: 'Action',
        align: 'right',
        width: 70,
        render: (_, record) => (
          <div className="d-flex">
            <ActionBox item={record} onDelete={() => onDelete(record?._id)} />
          </div>
        ),
      },
    ],
    [navigate, selected, data],
  )

  const updateSelection = flag => {
    const selected = flag ? data.map(item => item._id) : []
    setSelected(selected)
  }

  const handleMultipleReview = () => {
    if (!selected?.length) {
      notification.warning({ message: 'Select Video' })
      return
    }
    const filteredVideos = data.filter(value => selected.includes(value?._id))
    setOpenModal({
      open: true,
      data: selected?.length === 1 ? filteredVideos[0] : filteredVideos,
      mode: selected?.length === 1 ? 'single' : 'multiple',
    })
  }
  const handleFilter = (key, value) => {
    updateFilter(key, value)
  }
  return (
    <VideoCardContainer className="row">
      <FlexRowBetween>
        <FlexRow>
          <SortButton
            value={filter?.sort}
            onSelect={value => handleFilter('sort', value)}
          />
          <InputSearchField
            placeholder="Search here..."
            onChange={val => setSearch(val)}
            className="ms-2 w-50"
          />
        </FlexRow>

        <div className="d-flex align-items-center">
          {selected?.length > 0 && (
            <PrimaryButton onClick={handleMultipleReview} className="me-3">
              Review
            </PrimaryButton>
          )}
          <div className="d-flex">
            <FilterButton handleFilter={handleFilter} component="video" />
            <ToggleViewButton onToggle={view => setView(view)} />
          </div>
        </div>
      </FlexRowBetween>

      {view === 'grid' ? (
        <VideoGridView
          openReviewModal={record =>
            setOpenReviewModal({
              open: true,
              data: record,
              mode: 'single',
            })
          }
          data={data}
          onOpen={onOpen}
          onRestore={onDelete}
          filter={filter}
          total={total}
          pageChanged={pageChanged}
          pageSizeChanged={pageSizeChanged}
        />
      ) : (
        <div className="my-3">
          <CustomTable
            data={data}
            columns={columns}
            pageChanged={pageChanged}
            pageSizeChanged={pageSizeChanged}
            page={filter?.page}
            pageSize={filter?.limit}
            updateFilter={updateFilter}
            onAllSelect={updateSelection}
            loading={loading}
            total={total}
            hideSorting
          />
        </div>
      )}
      {loading && <LoaderBar />}
      {!loading && !data.length && <Empty />}
      {openReviewModal.open && (
        <ReviewReportedVideoModal
          visible={openReviewModal.open}
          data={openReviewModal.data}
          mode={openReviewModal.mode}
          refetch={refetch}
          selected={selected}
          setSelected={setSelected}
          onCancel={() =>
            setOpenReviewModal({ data: null, mode: 'single', open: false })
          }
        />
      )}
      {openModal.open && (
        <VideoModal
          visible
          onClose={() => setOpenModal({ open: false, item: {} })}
          item={openModal.item}
        />
      )}
    </VideoCardContainer>
  )
}

export const DeletedVideosList = () => {
  const {
    data: DeletedData,
    filter,
    pageChanged,
    pageSizeChanged,
    total: dataTotal,
    loading,
    updateFilter,
  } = GetDeletedVideosList()
  const navigate = useNavigate()
  const [data, setData] = useState([])
  const [view, setView] = useState('grid')
  const [openModal, setOpenModal] = useState({
    open: false,
    item: {},
  })
  const [total, setTotal] = useState(0)
  const [search, setSearch] = useState()
  const finalSearch = useDebounce(search, 1000)

  useEffect(() => {
    if (finalSearch?.trim() || finalSearch?.trim() === '') {
      updateFilter('search', finalSearch?.trim())
    }
  }, [finalSearch])

  useEffect(() => {
    if (DeletedData) {
      setData(DeletedData)
      setTotal(dataTotal)
    }
  }, [DeletedData])
  const onRestore = async id => {
    try {
      await VideosService.toggleBlock(id, true)
      setData(prevData =>
        prevData.filter(item => String(item._id) !== String(id)),
      )
      setTotal(dataTotal - 1)
    } catch (error) {
      notification.error({ message: 'Something went wrong' })
    }
  }

  const columns = useMemo(
    () => [
      {
        title: 'Video',
        dataIndex: 'video',
        key: 'title',
        width: 150,
        render: (_, record) => (
          <VideoWithTitle
            onClick={() => onOpen(record)}
            thumbnailUrl={record?.thumbnail_url}
            title={record?.title}
          />
        ),
      },
      ...commonColumns,
      {
        title: 'Action',
        dataIndex: 'Action',
        key: 'Action',
        align: 'right',
        width: 100,
        render: (_, record) => (
          <div className="d-flex">
            <ActionBox item={record} onRestore={() => onRestore(record?._id)} />
          </div>
        ),
      },
    ],
    [navigate, data],
  )

  const handleFilter = (key, value) => {
    updateFilter(key, value)
  }

  const onOpen = video => {
    setOpenModal({
      open: true,
      item: video,
    })
  }
  return (
    <VideoCardContainer className="row">
      <FlexRowBetween>
        <FlexRow>
          <SortButton
            value={filter.sort}
            onSelect={value => handleFilter('sort', value)}
          />
          <InputSearchField
            placeholder="Search here..."
            onChange={val => setSearch(val)}
            className="ms-2 w-50"
          />
        </FlexRow>
        <ToggleViewButton onToggle={setView} />
      </FlexRowBetween>
      {view === 'grid' ? (
        <VideoGridView
          data={data}
          onOpen={onOpen}
          onRestore={onRestore}
          filter={filter}
          total={total}
          pageChanged={pageChanged}
          pageSizeChanged={pageSizeChanged}
        />
      ) : (
        <div className="my-3">
          <CustomTable
            data={data}
            columns={columns}
            pageChanged={pageChanged}
            pageSizeChanged={pageSizeChanged}
            page={filter?.page}
            updateFilter={updateFilter}
            pageSize={filter?.limit}
            loading={loading}
            total={total}
            hideSorting
          />
        </div>
      )}
      {loading && <LoaderBar />}
      {!loading && !data.length && <Empty />}
      {openModal.open && (
        <VideoModal
          visible
          onClose={() => setOpenModal({ open: false, item: {} })}
          item={openModal.item}
        />
      )}
    </VideoCardContainer>
  )
}
